import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");
type DataCase = {
  id: number; effectiveAt: string; deadlineAt: string;
  requests: { id: number; kind: string; createdAt: string; acknowledgedAt: string | null; deliveredAt: string | null; completedAt: string | null }[];
};

export function MerchantDataRequests() {
  const queryClient = useQueryClient();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const { data, isLoading } = useQuery<DataCase | null>({
    queryKey: ["data-request-case"],
    queryFn: async () => {
      const res = await fetch(`${BASE}/api/data-requests/mine`, { credentials: "include" });
      if (!res.ok) throw new Error("Unable to load your data requests");
      return res.json();
    },
    staleTime: 30000,
  });
  const request = async (kind: "export" | "deletion") => {
    if (!data || !window.confirm(`Send a ${kind} request to support?`)) return;
    setPending(true); setError("");
    try {
      const res = await fetch(`${BASE}/api/data-requests/cases/${data.id}/requests`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ kind }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || "Request failed");
      await queryClient.invalidateQueries({ queryKey: ["data-request-case"] });
    } catch (err) { setError(err instanceof Error ? err.message : "Request failed"); }
    finally { setPending(false); }
  };
  return <Card>
    <CardHeader><CardTitle className="text-base">Data export & deletion</CardTitle></CardHeader>
    <CardContent className="space-y-3 text-sm">
      {isLoading ? <p>Loading requests…</p> : data ? <>
        <p>Cancellation effective: {new Date(data.effectiveAt).toLocaleString()}. Request and receive your export by {new Date(data.deadlineAt).toLocaleString()}. Support handles delivery and deletion manually.</p>
        <div className="flex gap-2 flex-wrap">
          <Button size="sm" disabled={pending || Date.now() >= new Date(data.deadlineAt).getTime()} onClick={() => void request("export")}>Request export</Button>
          <Button size="sm" variant="outline" disabled={pending} onClick={() => void request("deletion")}>Request deletion</Button>
        </div>
        {data.requests.map(r => <p key={r.id}>#{r.id} {r.kind}: received {new Date(r.createdAt).toLocaleString()} · {r.acknowledgedAt ? "acknowledged" : "awaiting acknowledgement"} · {r.kind === "export" ? r.deliveredAt ? "delivered" : "delivery pending" : r.completedAt ? "completed" : "deletion pending"}</p>)}
      </> : <p>To cancel or request a copy of your data, email <a className="underline" href="mailto:support@retailpulsepos.com">support@retailpulsepos.com</a>. Support will verify your identity and record your effective cancellation date.</p>}
      {error && <p role="alert" className="text-destructive">{error}</p>}
    </CardContent>
  </Card>;
}